/**
 * chatApi — REST helpers for the Team Chat UI.
 *
 * Route strategy:
 *   - Web routes (/chat/) for all reads and mutations: rooms list, messages
 *     list/create/update/destroy, read/delivered marks, reactions, search,
 *     @mention person search, typing indicators, and task card previews.
 *     These controllers respond to both HTML (Hotwire) and JSON, use session
 *     auth, and trigger the correct server-side push and badge logic.
 *   - API routes (/api/chat/v1/) for endpoints with NO web equivalent:
 *       · presign     — attachment DirectUpload presign
 *       · finalize    — associate uploaded blobs with a message
 *       · download    — returns a URL string, not a fetch (URL helper only)
 *
 * All methods return FetchResponse from @rails/request.js.
 * JSON responses: await response.json
 * Turbo Stream responses: rendered automatically.
 *
 * Note: This file intentionally uses @rails/request.js instead of the shared
 * doFetch helper. @rails/request.js handles Rails CSRF token injection,
 * Turbo Stream response handling, and session-cookie auth automatically —
 * these are Rails-specific concerns that don't apply to the React Native client.
 */

import { request } from '../../lib/request'
import { REQUEST_TIMEOUT_MS } from '@tshmanager/chat-shared'
import {
  chat_rooms_path,
  chat_room_path,
  purge_chat_room_path,
  chat_room_messages_path,
  chat_room_message_path,
  chat_message_path,
  mark_as_read_chat_room_messages_path,
  delivered_chat_room_message_path,
  chat_room_message_reaction_path,
  chat_search_path,
  chat_room_persons_path,
  chat_room_typing_index_path,
  chat_task_path,
  chat_directs_path,
  // API-only routes — no web equivalents
  presign_api_chat_v1_room_attachments_path,
  finalize_api_chat_v1_room_message_attachments_path,
  api_chat_v1_attachment_download_path,
} from '../../routes'

export const chatApi = {
  // ── Rooms ──────────────────────────────────────────────────────────────
  rooms: {
    /** GET /chat/rooms → Room[] */
    list: () =>
      request.get(chat_rooms_path(), { responseKind: 'json' }),

    /**
     * DELETE /chat/rooms/:id
     * Leaves the room (destroys current person's membership). Room is destroyed
     * only when no members remain. Same semantics as mobile api.rooms.delete.
     */
    destroy: (id) =>
      request.delete(chat_room_path(id), { responseKind: 'json' }),

    /**
     * DELETE /chat/rooms/:id/purge
     * Permanently destroys the room for every member (messages + memberships).
     */
    purge: (id) =>
      request.delete(purge_chat_room_path(id), { responseKind: 'json' }),
  },

  // ── Messages ───────────────────────────────────────────────────────────
  messages: {
    /**
     * GET /chat/rooms/:roomId/messages?before=:before
     * Returns a page of messages older than `before` (cursor-based pagination).
     */
    list: (roomId, { before } = {}) => {
      const query = before ? `?before=${before}` : ''
      return request.get(`${chat_room_messages_path(roomId)}${query}`, { responseKind: 'json' })
    },

    /**
     * GET /chat/rooms/:roomId/messages/:id → single message (JSON)
     * Used to poll for a document attachment's preview_url once the
     * server-side preview PNG finishes generating (preview_pending → preview_url).
     */
    show: (roomId, id) =>
      request.get(chat_room_message_path(roomId, id), { responseKind: 'json' }),

    /**
     * POST /chat/rooms/:roomId/messages
     * Body: { body, attachments: [], reply_to_id }
     */
    create: (roomId, body) =>
      request.post(chat_room_messages_path(roomId), {
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
        responseKind: 'json',
      }),

    /**
     * PATCH /chat/messages/:id
     * Body: { body }
     */
    update: (id, body) =>
      request.patch(chat_message_path(id), {
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
        responseKind: 'json',
      }),

    /**
     * DELETE /chat/messages/:id
     */
    destroy: (id) =>
      request.delete(chat_message_path(id), { responseKind: 'json' }),

    /**
     * POST /chat/rooms/:roomId/messages/mark_as_read
     * Marks all messages in the room as read for the current user.
     * Triggers broadcast_badge_silent_push! on the server side.
     */
    read: (roomId) =>
      request.post(mark_as_read_chat_room_messages_path(roomId), { responseKind: 'json' }),

    /**
     * POST /chat/rooms/:roomId/messages/:msgId/delivered
     * Marks a specific message as delivered.
     */
    delivered: (roomId, msgId) =>
      request.post(delivered_chat_room_message_path(roomId, msgId), { responseKind: 'json' }),
  },

  // ── Typing ─────────────────────────────────────────────────────────────
  typing: {
    /**
     * POST /chat/rooms/:roomId/typing
     * Body: { typing: Boolean }
     * Uses web session auth (no Cognito token needed from the browser).
     */
    send: (roomId, typing) =>
      request.post(chat_room_typing_index_path(roomId), {
        body: JSON.stringify({ typing }),
        headers: { 'Content-Type': 'application/json' },
        responseKind: 'json',
      }),
  },

  // ── Reactions ──────────────────────────────────────────────────────────
  reactions: {
    /**
     * POST /chat/rooms/:roomId/messages/:msgId/reaction
     * Body: { emoji }
     * Toggles the reaction (adds if absent, removes if present).
     */
    toggle: (roomId, msgId, emoji) =>
      request.post(chat_room_message_reaction_path(roomId, msgId), {
        body: JSON.stringify({ emoji }),
        headers: { 'Content-Type': 'application/json' },
        responseKind: 'json',
      }),
  },

  // ── Attachments — API routes (presign/finalize/download only on API namespace) ──
  attachments: {
    /**
     * POST /api/chat/v1/rooms/:roomId/attachments/presign
     * Body: { filename, content_type, byte_size, checksum }
     * Returns: { direct_upload: { url, headers }, blob: { signed_id, ... } }
     */
    presign: (roomId, params) =>
      request.post(presign_api_chat_v1_room_attachments_path(roomId), {
        body: JSON.stringify(params),
        headers: { 'Content-Type': 'application/json' },
        responseKind: 'json',
      }),

    /**
     * POST /api/chat/v1/rooms/:roomId/messages/:msgId/attachments/finalize
     * Body: { signed_ids: [] }
     * Associates uploaded blobs with the message after DirectUpload completes.
     */
    finalize: (roomId, msgId, params) =>
      request.post(finalize_api_chat_v1_room_message_attachments_path(roomId, msgId), {
        body: JSON.stringify(params),
        headers: { 'Content-Type': 'application/json' },
        responseKind: 'json',
      }),

    /**
     * Returns the download URL for a given ActiveStorage signed_id.
     * Not a fetch — returns a URL string for <a href> or window.open.
     */
    download: (signedId) => api_chat_v1_attachment_download_path(signedId),
  },

  // ── Search ─────────────────────────────────────────────────────────────
  search: {
    /**
     * GET /chat/search?q=:q → Room[]
     * Full-text room search (name, last message).
     */
    rooms: (q) =>
      request.get(chat_search_path(), {
        responseKind: 'json',
        query: { q },
      }),
  },

  // ── Persons ────────────────────────────────────────────────────────────
  persons: {
    /**
     * GET /chat/rooms/:roomId/persons?q=:q → Person[]
     * Person search for @mention typeahead in the Composer.
     */
    search: (roomId, q) =>
      request.get(chat_room_persons_path(roomId), {
        responseKind: 'json',
        query: { q },
      }),

    /**
     * GET /chat/rooms/:roomId/person_profiles/:id → PersonProfile
     * Returns Viber-style contact profile for a chat participant.
     * Shape: { id, name, email, phone_number, role, avatar_url, online, last_active_at }
     */
    profile: (roomId, personId) =>
      request.get(`/chat/rooms/${roomId}/person_profiles/${personId}`, {
        responseKind: 'json',
      }),
  },

  // ── Direct rooms ───────────────────────────────────────────────────────
  directs: {
    /**
     * POST /chat/rooms/directs with person_id → Room
     * Opens an existing 1:1 direct room or creates one (idempotent).
     * Returns the serialized room shape so the SPA can navigate to it.
     */
    open: (personId) =>
      request.post(chat_directs_path(), {
        body: JSON.stringify({ person_id: personId }),
        headers: { 'Content-Type': 'application/json' },
        responseKind: 'json',
      }),
  },

  // ── Tasks ──────────────────────────────────────────────────────────────
  tasks: {
    /**
     * GET /chat/tasks/:id → Task (reference card shape)
     * Used by TaskCard.jsx to fetch task details inline in messages.
     */
    show: (id) =>
      request.get(chat_task_path(id), { responseKind: 'json' }),
  },
}

// Re-export shared ApiError so callers can do `instanceof ApiError` checks
// without importing from the shared package directly.
export { ApiError } from '@tshmanager/chat-shared'

// REQUEST_TIMEOUT_MS is exported for any component that sets custom timeouts
// (e.g. long-running attachment upload progress checks).
export { REQUEST_TIMEOUT_MS }
